import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    FormControl, Grid, Box, Typography, Stepper, Step, StepLabel, StepContent,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Autocomplete, TextField,
    Table, TableHead, TableRow, TableBody, TableCell, Chip, Avatar, IconButton, Tooltip
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getIdRequest, getRequest, getPatientOrdersRequest, resetgetPatientOrders, resetGetId } from '../../../redux/modules/orders/slice';
import moment from 'moment';
import AppointmentCompleted from './appointmentCompleted';
import { setMainHeading } from '../../../redux/modules/mainHeading/slice';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CloseIcon from '@mui/icons-material/Close';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
    maxWidth: 1400,
}));

const SearchCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
}));

const RecentProceduresCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
}));

const RecentItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.subtle || theme.palette.grey[50],
    marginBottom: theme.spacing(1.5),
    cursor: 'pointer',
    transition: 'all 150ms ease',
    '&:hover': {
        backgroundColor: 'rgba(105, 203, 249, 0.1)',
        transform: 'translateX(4px)',
    },
    '&:last-child': {
        marginBottom: 0,
    },
}));

const InfoCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
}));

const InfoValue = styled(Typography)(({ theme }) => ({
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 10,
        backgroundColor: theme.palette.background.subtle || theme.palette.grey[50],
        '& fieldset': {
            borderColor: 'transparent',
        },
        '&:hover fieldset': {
            borderColor: theme.palette.divider,
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
        },
    },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
    const colors = {
        'Appointment Complete': { bg: 'rgba(16, 185, 129, 0.1)', color: '#10B981' },
        'Requested': { bg: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B' },
        'In Progress': { bg: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' },
    };
    const colorSet = colors[status] || { bg: 'rgba(107, 114, 128, 0.1)', color: '#6B7280' };
    return {
        backgroundColor: colorSet.bg,
        color: colorSet.color,
        fontWeight: 500,
        fontSize: '0.75rem',
    };
});

const StyledStepper = styled(Stepper)(({ theme }) => ({
    '& .MuiStepLabel-root': {
        padding: theme.spacing(2, 0),
    },
    '& .MuiStepLabel-label': {
        fontSize: '1rem',
        fontWeight: 600,
    },
    '& .MuiStepIcon-root': {
        width: 32,
        height: 32,
        '&.Mui-active': {
            color: theme.palette.primary.main,
        },
        '&.Mui-completed': {
            color: '#10B981',
        },
    },
}));

const ProcedureTracking = () => {
    const dispatch = useDispatch();
    let { getRes, getIdRes, getPatientOrders } = useSelector(state => state.orders);
    const [selectedOrder, setSelectedOrder] = useState([]);
    const [patientOrders, setPatientOrders] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedValue, setSelectedValue] = useState(null);
    const [orderModal, setOrderModal] = useState(false);
    const [recentProcedures, setRecentProcedures] = useState([]);

    const { currentBranch } = useSelector(state => state.branch);

    useEffect(() => {
        if (currentBranch && currentBranch?.id) {
            setSelectedValue(null);
            setSelectedOrder(null);
        }
    }, [currentBranch?.id]);

    useEffect(() => {
        dispatch(setMainHeading("Procedure Tracking"));
        if (getIdRes?.success) {
            const list = getIdRes?.data[0]?.radiologyId?.examList?.map((item) => item.list).flat();
            let activeStep = 0;
            const currentTimestamp = moment();
            const appoinmentTime = moment(getIdRes?.data[0]?.radiologyId?.appoinmentId?.startTime);
            if (getIdRes?.data[0]?.appoinmentCompleteStaffId && getIdRes?.data[0]?.status === "Appointment Complete") {
                activeStep = 2;
            } else if (currentTimestamp.isAfter(appoinmentTime)) {
                activeStep = 1;
            }
            const procedureData = {
                orderId: getIdRes?.data[0]?.orderId,
                status: getIdRes?.data[0]?.status,
                appoinmentCompleteStaffId: getIdRes?.data[0]?.appoinmentCompleteStaffId,
                appoinmentCompleteDescription: getIdRes?.data[0]?.appoinmentCompleteDescription,
                appoinment: getIdRes?.data[0]?.radiologyId?.appoinmentId,
                patient: getIdRes?.data[0]?.patientId,
                examList: list,
                activeStep,
            };
            setSelectedValue(procedureData);

            // Add to recent procedures (keep last 3)
            setRecentProcedures(prev => {
                const filtered = prev.filter(p => p.orderId !== procedureData.orderId);
                return [procedureData, ...filtered].slice(0, 3);
            });

            dispatch(resetGetId());
        } else if (getPatientOrders?.success) {
            setOrderModal(true);
            setPatientOrders(getPatientOrders?.data);
            dispatch(resetgetPatientOrders());
        }
    }, [dispatch, getIdRes, getPatientOrders]);

    const handleSelect = (selected) => {
        if (selected) {
            setSelectedOrder(selected);
            setSelectedPatient(null);
            dispatch(getIdRequest(selected.orderId));
        }
    };

    const handlepatientSelect = (selected) => {
        if (selected) {
            setSelectedValue(null);
            setSelectedOrder(null);
            setSelectedPatient(selected);
            dispatch(getPatientOrdersRequest(selected?._id));
        }
    };

    const handleInputChange = async (value, elm) => {
        dispatch(getRequest({ value: value, elm: elm }));
    };

    const handleRecentClick = (procedure) => {
        setSelectedValue(procedure);
    };

    return (
        <PageContainer>
            {/* Search Section */}
            <SearchCard>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <SearchIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight={600}>
                        Find Procedure
                    </Typography>
                </Box>
                <Grid container spacing={3}>
                    <Grid item lg={5} md={6} sm={12} xs={12}>
                        <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 1 }}>
                            Search by Order ID
                        </Typography>
                        <StyledAutocomplete
                            value={selectedOrder}
                            options={getRes?.orders || []}
                            getOptionValue={(option) => option._id}
                            getOptionLabel={(option) => option?.orderId || ''}
                            onInputChange={(event, value) => handleInputChange(value, "order")}
                            onChange={(event, newValue) => handleSelect(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Enter order ID..."
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: <AssignmentIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item lg={7} md={6} sm={12} xs={12}>
                        <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 1 }}>
                            Search by Patient
                        </Typography>
                        <StyledAutocomplete
                            value={selectedPatient}
                            autoFocus={false}
                            options={getRes?.patients || []}
                            getOptionValue={(option) => option._id}
                            getOptionLabel={(option) => `${option?.fName} ${option?.lName} - ${option?.email}`}
                            onInputChange={(event, value) => handleInputChange(value, "patient")}
                            onChange={(event, newValue) => handlepatientSelect(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search patient name or email..."
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: <PersonIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </SearchCard>

            {/* Recent Procedures - Show only when no procedure is selected */}
            {!selectedValue && recentProcedures.length > 0 && (
                <RecentProceduresCard>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <HistoryIcon sx={{ color: 'text.secondary' }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                            Recently Viewed
                        </Typography>
                    </Box>
                    {recentProcedures.map((procedure, index) => (
                        <RecentItem key={index} onClick={() => handleRecentClick(procedure)}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', width: 40, height: 40 }}>
                                    {procedure.patient?.fName?.[0]}{procedure.patient?.lName?.[0]}
                                </Avatar>
                                <Box>
                                    <Typography fontWeight={500}>
                                        {procedure.patient?.fName} {procedure.patient?.lName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Order: {procedure.orderId} • {moment(procedure.appoinment?.startTime).format('MMM DD, YYYY')}
                                    </Typography>
                                </Box>
                            </Box>
                            <StatusChip
                                label={procedure.status || 'Pending'}
                                status={procedure.status}
                                size="small"
                            />
                        </RecentItem>
                    ))}
                </RecentProceduresCard>
            )}

            {/* Procedure Details */}
            {selectedValue && (
                <>
                    {/* Patient Information */}
                    <InfoCard>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48, fontSize: '1.125rem' }}>
                                    {selectedValue?.patient?.fName?.[0]}{selectedValue?.patient?.lName?.[0]}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight={600}>
                                        {selectedValue?.patient?.fName} {selectedValue?.patient?.lName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Order #{selectedValue?.orderId}
                                    </Typography>
                                </Box>
                            </Box>
                            <StatusChip
                                label={selectedValue?.status || 'In Progress'}
                                status={selectedValue?.status}
                            />
                        </Box>
                        <Grid container spacing={3}>
                            <Grid item lg={3} md={4} sm={6} xs={6}>
                                <InfoLabel>Email</InfoLabel>
                                <InfoValue>{selectedValue?.patient?.email || '—'}</InfoValue>
                            </Grid>
                            <Grid item lg={3} md={4} sm={6} xs={6}>
                                <InfoLabel>Gender</InfoLabel>
                                <InfoValue sx={{ textTransform: 'capitalize' }}>{selectedValue?.patient?.gender || '—'}</InfoValue>
                            </Grid>
                            <Grid item lg={3} md={4} sm={6} xs={6}>
                                <InfoLabel>Date of Birth</InfoLabel>
                                <InfoValue>{selectedValue?.patient?.dob || '—'}</InfoValue>
                            </Grid>
                            <Grid item lg={3} md={12} sm={6} xs={12}>
                                <InfoLabel>Examinations</InfoLabel>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                    {selectedValue?.examList?.map((item) => (
                                        <Chip
                                            key={item?._id}
                                            label={item?.name}
                                            size="small"
                                            sx={{ bgcolor: 'primary.light', color: 'primary.dark' }}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </InfoCard>

                    {/* Procedure Timeline */}
                    <InfoCard>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                            Procedure Timeline
                        </Typography>
                        <StyledStepper activeStep={selectedValue?.activeStep} orientation="vertical">
                            <Step>
                                <StepLabel
                                    StepIconComponent={() => (
                                        <Box sx={{
                                            width: 32, height: 32, borderRadius: '50%',
                                            bgcolor: selectedValue?.activeStep >= 0 ? '#10B981' : 'grey.300',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                            <CheckCircleIcon sx={{ color: '#fff', fontSize: 20 }} />
                                        </Box>
                                    )}
                                >
                                    <Box>
                                        <Typography fontWeight={600}>Appointment Scheduled</Typography>
                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                            <Grid item xs={12} sm={4}>
                                                <InfoLabel>Category</InfoLabel>
                                                <InfoValue>{selectedValue?.appoinment?.appoinmentCategory}</InfoValue>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <InfoLabel>Date & Time</InfoLabel>
                                                <InfoValue>{moment(selectedValue?.appoinment?.startTime).format("MMM DD, YYYY • hh:mm A")}</InfoValue>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <InfoLabel>Duration</InfoLabel>
                                                <InfoValue>{selectedValue?.appoinment?.appoinmentDuration} Minutes</InfoValue>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </StepLabel>
                            </Step>
                            <Step>
                                <StepLabel
                                    StepIconComponent={() => (
                                        <Box sx={{
                                            width: 32, height: 32, borderRadius: '50%',
                                            bgcolor: selectedValue?.activeStep >= 2 ? '#10B981' : selectedValue?.activeStep === 1 ? 'primary.main' : 'grey.300',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                            {selectedValue?.activeStep >= 2 ?
                                                <CheckCircleIcon sx={{ color: '#fff', fontSize: 20 }} /> :
                                                <ScheduleIcon sx={{ color: '#fff', fontSize: 20 }} />
                                            }
                                        </Box>
                                    )}
                                >
                                    <Typography fontWeight={600}>Examination Complete</Typography>
                                    {selectedValue?.appoinmentCompleteStaffId?._id && (
                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                            <Grid item xs={12} sm={4}>
                                                <InfoLabel>Completed By</InfoLabel>
                                                <InfoValue>{selectedValue?.appoinmentCompleteStaffId?.firstName} {selectedValue?.appoinmentCompleteStaffId?.lastName}</InfoValue>
                                            </Grid>
                                            <Grid item xs={12} sm={8}>
                                                <InfoLabel>Notes</InfoLabel>
                                                <InfoValue>{selectedValue?.appoinmentCompleteDescription || '—'}</InfoValue>
                                            </Grid>
                                        </Grid>
                                    )}
                                </StepLabel>
                                {!selectedValue?.appoinmentCompleteStaffId?._id && selectedValue?.activeStep === 1 && (
                                    <StepContent>
                                        <AppointmentCompleted orderId={selectedValue?.orderId} />
                                    </StepContent>
                                )}
                            </Step>
                            <Step>
                                <StepLabel>Image Processing</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>Report Generation</StepLabel>
                            </Step>
                        </StyledStepper>
                    </InfoCard>
                </>
            )}

            {/* Patient Orders Modal */}
            <Dialog maxWidth="lg" fullWidth open={orderModal} onClose={() => setOrderModal(false)}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={600}>Select Patient Order</Typography>
                    <IconButton onClick={() => setOrderModal(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'background.subtle' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Exams</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {patientOrders?.length > 0 ? patientOrders.map(elem => (
                                <TableRow key={elem._id} hover>
                                    <TableCell>
                                        {moment(elem?.radiologyId?.appoinmentId?.startTime).format('MMM DD, YYYY • hh:mm A')}
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontFamily: 'monospace' }}>{elem?.orderId}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        {elem?.radiologyId?.examList?.map((item) => item.list.map((listItem) => listItem?.name)).flat().join(", ")}
                                    </TableCell>
                                    <TableCell>
                                        <StatusChip label={elem?.status || 'Pending'} status={elem?.status} size="small" />
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            sx={{ borderRadius: 2 }}
                                            onClick={() => {
                                                const list = elem?.radiologyId?.examList?.map((item) => item.list).flat();
                                                let activeStep = 0;
                                                const currentTimestamp = moment();
                                                const appoinmentTime = moment(elem?.radiologyId?.appoinmentId?.startTime);
                                                if (elem?.appoinmentCompleteStaffId && elem?.status === "Appointment Complete") {
                                                    activeStep = 2;
                                                } else if (currentTimestamp.isAfter(appoinmentTime)) {
                                                    activeStep = 1;
                                                }
                                                setSelectedValue({
                                                    orderId: elem?.orderId,
                                                    status: elem?.status,
                                                    appoinmentCompleteStaffId: elem?.appoinmentCompleteStaffId,
                                                    appoinmentCompleteDescription: elem?.appoinmentCompleteDescription,
                                                    appoinment: elem?.radiologyId?.appoinmentId,
                                                    patient: selectedPatient,
                                                    examList: list,
                                                    activeStep
                                                });
                                                setOrderModal(false);
                                            }}
                                        >
                                            Select
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">No orders found for this patient</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
};

export default ProcedureTracking;
